import * as React from "react";
import { Button } from "./ui/button";
import { reportError } from "@/utils/error-reporting";
import { redactString } from "@/utils/redact";
import { CopyDiagnosticsButton } from "./CopyDiagnosticsButton";

/* global console */

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Shown above the message. Defaults to a generic title. */
  title?: string;
  /** Identifies where the failure happened, e.g. "research-chat". Included in reports. */
  area: string;
  /**
   * When this value changes the boundary resets and retries its children.
   * Pass something that represents "the user moved on", e.g. the selected tab.
   */
  resetKey?: string | number | null;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Stops one broken screen taking down the whole task pane.
 *
 * Without a boundary anywhere in the tree, React 18 unmounts EVERYTHING when a
 * render throws. Inside an Office task pane that leaves a blank white panel with
 * no message and no way back — the user has to close and reopen Word. That is
 * exactly what customers saw when opening a research chat.
 *
 * React.Suspense does NOT do this job: it only handles a pending lazy import.
 * If a lazy chunk fails to LOAD, or the module throws while evaluating, the
 * rejection is thrown to the nearest error boundary — and if there is none, the
 * tree dies. So every lazy boundary needs one of these above it.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Let the user escape by navigating elsewhere rather than restarting Word.
    if (this.state.error !== null && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[${this.props.area}] crashed:`, error, info.componentStack);
    reportError(error, {
      area: this.props.area,
      componentStack: info.componentStack ?? undefined,
    });
  }

  private handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="flex flex-col items-center justify-center flex-1 min-h-0 gap-3 p-6 text-center">
        <p className="text-sm font-medium text-neutral-700">
          {this.props.title ?? "Something went wrong"}
        </p>
        <p className="text-xs text-neutral-500">
          This part of Quillio stopped responding. The rest of the add-in still works.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={this.handleRetry}>
            Try again
          </Button>
          {/* So support gets the actual error instead of "it went blank". */}
          <CopyDiagnosticsButton variant="ghost" size="sm" />
        </div>
        {/* Surfaced so a customer can read the cause back to support. */}
        <details className="mt-1 w-full text-left">
          <summary className="text-xs text-neutral-400 cursor-pointer">Error details</summary>
          <p className="mt-1 break-words text-[10px] text-neutral-400 font-mono">
            {redactString(error.message || String(error))}
          </p>
        </details>
      </div>
    );
  }
}

export default ErrorBoundary;
